import { createAsyncThunk } from '@reduxjs/toolkit';
import axiosClient, { axiosWithAuth } from '@/services/axios';
import { orderStateTypes, shippingMethodTypes } from '@/constants/types';
import { getPhoneWithCode } from '@/utils/string';

const getRedirectionUrl = ({ type, baseUrl }) =>
  `${baseUrl}/order-processing?type=${type}`;

const generatePayload = ({
  shippingMethod,
  currentAddress,
  cartToken,
  referralCode,
  paymentMethod,
  currentPromoCode,
  storeCode,
}) => {
  let updatedPayload = { cart_token: cartToken };

  if (currentAddress && currentAddress?.id !== '') {
    updatedPayload = {
      ...updatedPayload,
      shipping_address_id: currentAddress.id,
    };
  }

  if (paymentMethod && paymentMethod?.value !== '') {
    updatedPayload = { ...updatedPayload, payment_method: paymentMethod.value };
  }

  if (currentPromoCode && currentPromoCode !== '') {
    updatedPayload = { ...updatedPayload, coupon_code: currentPromoCode };
  }

  if (referralCode) {
    updatedPayload = {
      ...updatedPayload,
      referal_code: referralCode,
    };
  }

  if (
    shippingMethod &&
    shippingMethod === shippingMethodTypes.storePickup &&
    storeCode
  ) {
    updatedPayload = {
      ...updatedPayload,
      shipping_method: shippingMethod,
      store_code: storeCode,
    };
    delete updatedPayload.referal_code;
  } else {
    updatedPayload = {
      ...updatedPayload,
      shipping_method: shippingMethodTypes.regular,
    };
  }

  return updatedPayload;
};

export const getInitCheckoutSummary = createAsyncThunk(
  'checkoutSummary/getInitCheckoutSummary',
  async (_, { getState, rejectWithValue }) => {
    try {
      const { isLoggedIn } = getState().auth;
      const { cartToken, items } = getState().carts;
      const { currentAddress } = getState().shippingAddress;
      const {
        paymentMethod,
        currentPromoCode,
        referralCode,
        selectedShippingMethod,
      } = getState().checkoutSummary;
      const { selectedStore } = getState().storeLocator;

      if (!cartToken || !Array.isArray(items)) {
        throw new Error('Invalid cart token or cart items');
      }

      const payload = {
        state: orderStateTypes.init,
        ...generatePayload({
          shippingMethod: selectedShippingMethod,
          currentAddress,
          cartToken,
          referralCode,
          paymentMethod,
          currentPromoCode,
          storeCode: selectedStore?.store_code ?? null,
        }),
      };

      let response;
      if (isLoggedIn) {
        response = await axiosWithAuth().post(
          '/api/order/checkout-summary',
          payload,
        );
      } else {
        response = await axiosClient.post(
          '/api/order/checkout-summary',
          payload,
        );
      }

      return await response.data;
    } catch (error) {
      console.log({ error });

      return rejectWithValue(
        error?.response?.data?.error?.message || 'Failed to initialize',
      );
    }
  },
);

export const getUpdateCheckoutSummary = createAsyncThunk(
  'checkoutSummary/getUpdateCheckoutSummary',
  async (_, { getState, rejectWithValue }) => {
    try {
      const { isLoggedIn } = getState().auth;
      const { cartToken, items } = getState().carts;
      const { currentAddress } = getState().shippingAddress;
      const {
        orderToken,
        paymentMethod,
        currentPromoCode,
        referralCode,
        selectedShippingMethod,
      } = getState().checkoutSummary;

      const { selectedStore } = getState().storeLocator;

      if (!cartToken || !Array.isArray(items)) {
        throw new Error('Invalid cart token or cart items');
      }
      if (!orderToken) {
        throw new Error('Invalid order token');
      }

      const payload = {
        token: orderToken,
        state: orderStateTypes.update,
        ...generatePayload({
          shippingMethod: selectedShippingMethod,
          currentAddress,
          cartToken,
          referralCode,
          paymentMethod,
          currentPromoCode,
          storeCode: selectedStore?.store_code ?? null,
        }),
      };

      let response;
      if (isLoggedIn) {
        response = await axiosWithAuth().post(
          '/api/order/checkout-summary',
          payload,
        );
      } else {
        response = await axiosClient.post(
          '/api/order/checkout-summary',
          payload,
        );
      }

      return await response.data;
    } catch (error) {
      console.log({ error });

      return rejectWithValue(
        error?.response?.data?.error?.message || 'Failed to updated',
      );
    }
  },
);

export const getPlaceCheckoutSummary = createAsyncThunk(
  'checkoutSummary/getPlaceCheckoutSummary',
  async ({ baseUrl }, { getState, rejectWithValue }) => {
    try {
      const { cartToken, items } = getState().carts;
      const { isLoggedIn } = getState().auth;
      const { currentAddress, isSelectDifferentBilling, billingAddress } =
        getState().shippingAddress;
      const {
        orderToken,
        paymentMethod,
        currentPromoCode,
        referralCode,
        type,
        selectedShippingMethod,
      } = getState().checkoutSummary;
      const { selectedStore } = getState().storeLocator;

      if (!cartToken || !Array.isArray(items)) {
        throw new Error('Invalid cart token or cart items');
      }

      if (!orderToken) {
        throw new Error('Invalid order token');
      }

      let payload = {
        token: orderToken,
        state: orderStateTypes.place,
        redirect_url: getRedirectionUrl({ type, baseUrl }),
        ...generatePayload({
          shippingMethod: selectedShippingMethod,
          currentAddress,
          cartToken,
          referralCode,
          paymentMethod,
          currentPromoCode,
          storeCode: selectedStore?.store_code ?? null,
        }),
      };

      if (isSelectDifferentBilling && billingAddress?.name) {
        payload = {
          ...payload,
          billing_address: {
            name: billingAddress.name,
            phone:
              billingAddress?.phone[0] === '+'
                ? billingAddress?.phone
                : getPhoneWithCode(billingAddress?.phone),
            email:
              billingAddress?.email?.length !== 0
                ? billingAddress?.email
                : null,
            address: billingAddress.address,
            floor: Number(billingAddress.floor) ?? 1,
            division_id: billingAddress?.division?.id,
            district_id: billingAddress?.district?.id,
            upazilla_id: billingAddress?.upazilla?.id,
            area_id: billingAddress?.area?.id,
            postal_code: billingAddress.postal_code,
          },
          same_as_shipping: false,
        };
      } else {
        const billingInfo = {
          name: currentAddress.name,
          phone: currentAddress.phone,
          email:
            currentAddress?.email?.length !== 0 ? currentAddress?.email : null,
          address: currentAddress.address,
          floor: Number(currentAddress.floor) ?? 1,
          division_id:
            currentAddress?.division?.id ?? currentAddress?.division_id,
          district_id:
            currentAddress?.district?.id ?? currentAddress?.district_id,
          upazilla_id:
            currentAddress?.upazilla?.id ?? currentAddress?.upazilla_id,
          area_id: currentAddress?.area?.id ?? currentAddress?.area_id,
          postal_code: currentAddress.postal_code,
        };
        payload = {
          ...payload,
          same_as_shipping: true,
          billing_address: billingInfo,
        };
      }

      let response;
      if (isLoggedIn) {
        response = await axiosWithAuth().post(
          '/api/order/checkout-summary',
          payload,
        );
      } else {
        response = await axiosClient.post(
          '/api/order/checkout-summary',
          payload,
        );
      }

      return await response.data;
    } catch (error) {
      console.log({ error });

      return rejectWithValue(
        error?.response?.data?.error?.message || 'Failed to place order',
      );
    }
  },
);

export const getBankEmiTenuresList = createAsyncThunk(
  'checkoutSummary/getBankEmiTenuresList',
  async ({ cartPrice }, { rejectWithValue }) => {
    try {
      if (!cartPrice === '') {
        throw new Error('Cart value can not be zero');
      }

      const response = await axiosClient.post(`/api/order/bank-emi/tenures`, {
        price: cartPrice,
      });

      return await response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const getAllBankList = createAsyncThunk(
  'checkoutSummary/getAllBankList',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get(`/api/order/all-bank-list`);

      return await response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

const prepareAvailableOfferRequestData = (items) => {
  if (Array.isArray(items) && items?.length > 0) {
    return items.map((item) => ({
      id: item?.id,
      cart_quantity: item?.cart_quantity,
    }));
  }

  return [];
};

export const getAvailableOfferList = createAsyncThunk(
  'checkoutSummary/getAvailableOfferList',
  async ({ items, cartTotal }, { rejectWithValue }) => {
    try {
      if (items?.length <= 0) {
        throw new Error('empty items');
      }

      const requestedItems = prepareAvailableOfferRequestData(items);

      if (requestedItems.length <= 0) {
        throw new Error('empty items');
      }

      const response = await axiosClient.post(
        '/api/checkout/available-offers',
        {
          products: requestedItems,
          cart_total: cartTotal ?? 0,
        },
      );

      return await response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);
